/**
 * Navigation component for handling authentication state across all pages
 */

import { auth } from '../services/api.js';

/**
 * Initialize navigation with authentication state
 */
export async function initNavigation() {
  await checkAuthStatus();
  setupMobileToggle();
}

function setupMobileToggle() {
  const header = document.querySelector('.site-header');
  const container = header?.querySelector('.container');
  if (!container) return;

  const toggle = document.createElement('button');
  toggle.className = 'nav-toggle';
  toggle.setAttribute('aria-label', 'Toggle navigation');
  toggle.innerHTML = '&#9776;';
  container.appendChild(toggle);

  toggle.addEventListener('click', (e) => {
    e.stopPropagation();
    header.classList.toggle('nav-open');
    toggle.innerHTML = header.classList.contains('nav-open') ? '&#10005;' : '&#9776;';
  });

  // Close on outside click
  document.addEventListener('click', () => {
    if (header.classList.contains('nav-open')) {
      header.classList.remove('nav-open');
      toggle.innerHTML = '&#9776;';
    }
  });

  // Close when a nav link is clicked
  document.querySelector('.main-nav')?.addEventListener('click', () => {
    header.classList.remove('nav-open');
    toggle.innerHTML = '&#9776;';
  });
}

/**
 * Check if user is authenticated and update UI accordingly
 */
async function checkAuthStatus() {
  console.log('Checking authentication status...');
  try {
    const user = await auth.getCurrentUser();
    console.log('User found:', user);
    if (user) {
      updateNavForAuthenticatedUser(user);
    } else {
      console.log('No user found');
      updateNavForUnauthenticatedUser();
    }
  } catch (error) {
    // User not authenticated, keep default nav
    console.log('Authentication check failed:', error);
    updateNavForUnauthenticatedUser();
  }
}

/**
 * Update navigation for authenticated users
 * Shows: Home / Search / [User Menu]
 */
function updateNavForAuthenticatedUser(user) {
  const nav = document.querySelector('.main-nav');
  if (!nav) return;
  
  // Clear existing navigation
  nav.innerHTML = '';
  
  // Add Home link
  const homeLink = document.createElement('a');
  homeLink.href = '/';
  homeLink.textContent = 'Home';
  if (window.location.pathname === '/' || window.location.pathname === '/index.html') {
    homeLink.classList.add('active');
  }
  nav.appendChild(homeLink);
  
  // Add Search link
  const searchLink = document.createElement('a');
  searchLink.href = '/search.html';
  searchLink.textContent = 'Search';
  if (window.location.pathname === '/search.html') {
    searchLink.classList.add('active');
  }
  nav.appendChild(searchLink);

  // Add How to Use link
  const howToLink = document.createElement('a');
  howToLink.href = '/how-to-use.html';
  howToLink.textContent = 'How to Use';
  if (window.location.pathname === '/how-to-use.html') {
    howToLink.classList.add('active');
  }
  nav.appendChild(howToLink);

  // Add About link
  const aboutLink = document.createElement('a');
  aboutLink.href = '/about.html';
  aboutLink.textContent = 'About Us';
  if (window.location.pathname === '/about.html') {
    aboutLink.classList.add('active');
  }
  nav.appendChild(aboutLink);

  // Add user menu
  const userMenu = createUserMenu(user);
  nav.appendChild(userMenu);
}

/**
 * Update navigation for unauthenticated users
 * Shows: Home / Search / Login
 */
function updateNavForUnauthenticatedUser() {
  const nav = document.querySelector('.main-nav');
  if (!nav) return;
  
  // Clear existing navigation
  nav.innerHTML = '';
  
  // Add Home link
  const homeLink = document.createElement('a');
  homeLink.href = '/';
  homeLink.textContent = 'Home';
  if (window.location.pathname === '/' || window.location.pathname === '/index.html') {
    homeLink.classList.add('active');
  }
  nav.appendChild(homeLink);
  
  // Add Search link
  const searchLink = document.createElement('a');
  searchLink.href = '/search.html';
  searchLink.textContent = 'Search';
  if (window.location.pathname === '/search.html') {
    searchLink.classList.add('active');
  }
  nav.appendChild(searchLink);

  // Add How to Use link
  const howToLink = document.createElement('a');
  howToLink.href = '/how-to-use.html';
  howToLink.textContent = 'How to Use';
  if (window.location.pathname === '/how-to-use.html') {
    howToLink.classList.add('active');
  }
  nav.appendChild(howToLink);

  // Add About link
  const aboutLink = document.createElement('a');
  aboutLink.href = '/about.html';
  aboutLink.textContent = 'About Us';
  if (window.location.pathname === '/about.html') {
    aboutLink.classList.add('active');
  }
  nav.appendChild(aboutLink);

  // Add Login link
  const loginLink = document.createElement('a');
  loginLink.href = '/login.html';
  loginLink.textContent = 'Login';
  if (window.location.pathname === '/login.html') {
    loginLink.classList.add('active');
  }
  nav.appendChild(loginLink);
}

/**
 * Create user menu for authenticated users
 * Menu items are role-based:
 * - All users: Dashboard, Profile, Submit Content
 * - Editors: Content Management, Quality Management
 * - Administrators: All of the above + User Management, Tag Management
 */
function createUserMenu(user) {
  const userMenu = document.createElement('div');
  userMenu.className = 'user-menu';
  
  const userButton = document.createElement('button');
  userButton.className = 'user-button';
  
  // Display user name with dropdown arrow
  const displayName = user.first_name && user.last_name 
    ? `${user.first_name} ${user.last_name}`
    : user.first_name || user.email.split('@')[0];
  userButton.innerHTML = `${displayName} ▼`;
  
  const dropdown = document.createElement('div');
  dropdown.className = 'user-dropdown hidden';
  
  // Get user role information
  const role = getUserRole(user);
  
  // Dashboard link (all authenticated users)
  const dashboardLink = document.createElement('a');
  dashboardLink.href = '/dashboard.html';
  dashboardLink.textContent = 'Dashboard';
  if (window.location.pathname === '/dashboard.html') {
    dashboardLink.classList.add('active');
  }
  dropdown.appendChild(dashboardLink);
  
  // Profile link (all authenticated users)
  const profileLink = document.createElement('a');
  profileLink.href = '/profile.html';
  profileLink.textContent = 'Profile';
  if (window.location.pathname === '/profile.html') {
    profileLink.classList.add('active');
  }
  dropdown.appendChild(profileLink);
  
  // Submit Content link (all authenticated users)
  const submitLink = document.createElement('a');
  submitLink.href = '/submit.html';
  submitLink.textContent = 'Submit Content';
  if (window.location.pathname === '/submit.html') {
    submitLink.classList.add('active');
  }
  dropdown.appendChild(submitLink);
  
  // Editor and Admin links
  if (role === 'editor' || role === 'administrator') {
    const editorSeparator = document.createElement('div');
    editorSeparator.className = 'menu-separator';
    dropdown.appendChild(editorSeparator);
    
    // Content Management (editors and admins)
    const contentMgmtLink = document.createElement('a');
    contentMgmtLink.href = '/admin-content.html';
    contentMgmtLink.textContent = 'Content Management';
    if (window.location.pathname === '/admin-content.html') {
      contentMgmtLink.classList.add('active');
    }
    dropdown.appendChild(contentMgmtLink);
    
    // Quality Management (editors and admins)
    const qualityMgmtLink = document.createElement('a');
    qualityMgmtLink.href = '/admin-quality.html';
    qualityMgmtLink.textContent = 'Quality Management';
    if (window.location.pathname === '/admin-quality.html') {
      qualityMgmtLink.classList.add('active');
    }
    dropdown.appendChild(qualityMgmtLink);
  }
  
  // Administrator-only links
  if (role === 'administrator') {
    const adminSeparator = document.createElement('div');
    adminSeparator.className = 'menu-separator';
    dropdown.appendChild(adminSeparator);
    
    // User Management (admins only)
    const userMgmtLink = document.createElement('a');
    userMgmtLink.href = '/admin-users.html';
    userMgmtLink.textContent = 'User Management';
    if (window.location.pathname === '/admin-users.html') {
      userMgmtLink.classList.add('active');
    }
    dropdown.appendChild(userMgmtLink);
    
    // Tag Management (admins only)
    const tagMgmtLink = document.createElement('a');
    tagMgmtLink.href = '/admin-tags.html';
    tagMgmtLink.textContent = 'Tag Management';
    if (window.location.pathname === '/admin-tags.html') {
      tagMgmtLink.classList.add('active');
    }
    dropdown.appendChild(tagMgmtLink);
  }
  
  // Separator before logout
  const separator = document.createElement('div');
  separator.className = 'menu-separator';
  dropdown.appendChild(separator);
  
  // Logout link
  const logoutLink = document.createElement('a');
  logoutLink.href = '#';
  logoutLink.textContent = 'Logout';
  logoutLink.addEventListener('click', handleLogout);
  dropdown.appendChild(logoutLink);
  
  userMenu.appendChild(userButton);
  userMenu.appendChild(dropdown);
  
  // Toggle dropdown
  userButton.addEventListener('click', (e) => {
    e.stopPropagation();
    dropdown.classList.toggle('hidden');
  });
  
  // Close dropdown when clicking outside
  document.addEventListener('click', () => {
    dropdown.classList.add('hidden');
  });
  
  return userMenu;
}

/**
 * Handle user logout
 */
async function handleLogout(e) {
  e.preventDefault();
  
  try {
    await auth.logout();
    
    // Redirect to home page
    window.location.href = '/';
  } catch (error) {
    console.error('Logout error:', error);
    // Force redirect even if logout fails
    window.location.href = '/';
  }
}

/**
 * Get user role from user object
 * @param {Object} user - User object with role information
 * @returns {string} Role name: 'administrator', 'editor', 'submitter', or 'public'
 */
function getUserRole(user) {
  if (!user || !user.role) return 'public';
  
  // Get role name (case-insensitive)
  const roleName = (user.role.name || user.role).toLowerCase();
  
  // Map role names to standard roles
  if (roleName === 'administrator' || roleName === 'admin') {
    return 'administrator';
  }
  
  if (roleName === 'editor') {
    return 'editor';
  }
  
  if (roleName === 'submitter') {
    return 'submitter';
  }
  
  // Default to public for unknown roles
  return 'public';
}